import { useEffect, useRef, useCallback, useState } from 'react';
import stripeEngine from '../../../payments/core/engines/stripe';

/**
 * useStripeExpressWalletSdk hook.
 * Manage the Stripe Express Checkout Element (ECE) lifecycle for a single express wallet
 * (Google Pay, Apple Pay, …): mount, unmount, amount updates, and wallet availability via the
 * ECE `ready` event (the Stripe-native equivalent of `canMakePayments`).
 *
 * The wallet is selected entirely through parameters, so the same primitive serves any wallet
 * without engine changes:
 * - `paymentMethods` is the ECE `paymentMethods` config (e.g. `{ googlePay: 'always', … }`).
 * - `walletKey` is the key read from the `ready` event's `availablePaymentMethods` to resolve
 *   real availability (e.g. `googlePay`, `applePay`).
 * - `errorPrefix` namespaces the wallet-specific error codes (`{prefix}_not_enabled`,
 *   `{prefix}_initialization_error`, `{prefix}_not_available`). `stripe_not_initialized` is
 *   wallet-agnostic.
 *
 * This hook is framework-agnostic (no Redux, i18n, or error monitoring) and receives all
 * configuration and callbacks as plain parameters.
 *
 * @param {object} props - Wallet config and callbacks.
 * @param {object} props.paymentMethods - ECE `paymentMethods` config for this wallet.
 * @param {string} props.walletKey - Key read from `availablePaymentMethods` on the `ready` event.
 * @param {string} props.errorPrefix - Prefix for the wallet-specific status/error codes.
 * @param {boolean} props.enabled - Whether this wallet is enabled for the tenant.
 * @param {number} props.amount - Numeric purchase total in regular currency units.
 * @param {string} props.currency - ISO currency code (e.g. "MXN").
 * @param {object} [props.appearance] - Stripe Appearance API configuration.
 * @param {string} [props.locale] - App locale; normalized by the engine on mount.
 * @param {Function} props.onClick - Callback for the ECE `click` event (validation gate).
 * @param {Function} props.onConfirm - Callback for the ECE `confirm` event (payment execution).
 * @param {Function} [props.onCancel] - Callback for the ECE `cancel` event (sheet dismissed).
 * @param {Function} [props.onReady] - Extra callback for the ECE `ready` event.
 * @returns {object} Express wallet ECE state and container ref.
 */
function useStripeExpressWalletSdk({
  paymentMethods,
  walletKey,
  errorPrefix,
  enabled,
  amount,
  currency,
  appearance,
  locale,
  onClick,
  onConfirm,
  onCancel,
  onReady,
}) {
  const containerRef = useRef(null);
  const isMountedRef = useRef(false);
  const mountedConfig = useRef({ amount: null, currency: null });

  // Re-run the mount effect when the amount first becomes available (falsy -> valid)
  // without remounting on every price change; those are handled by updateExpressAmount below.
  const hasAmount = Boolean(amount);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setErrorState] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);

  // Keep the latest callbacks in a ref so the mount effect never depends on their identity.
  // The ECE is mounted once and the stable wrappers below always invoke the most recent handler;
  // this prevents the Express Checkout Element from tearing down and remounting whenever a
  // callback is recreated (e.g. onConfirm changes on every purchaser-form keystroke because it
  // reads the redux-form purchaser values).
  const callbacksRef = useRef({});
  callbacksRef.current = { onClick, onConfirm, onCancel, onReady };

  // Track the latest amount/currency so the async mount() reads current values instead of the
  // ones captured when the effect started. `amount` is intentionally not a mount-effect
  // dependency, so the total can change while waitForStripe() is still pending; without this
  // the ECE would mount with a stale amount and never get corrected (the update effect below
  // already ran while isMountedRef was false). `currency` is a dependency and thus protected by
  // the cancelled guard, but it is tracked here too so mountedConfig matches what was mounted.
  const latestConfigRef = useRef({ amount, currency });
  latestConfigRef.current = { amount, currency };

  /**
   * Stable wrapper for the ECE `click` event; forwards to the latest onClick.
   * @param {Object} event - ECE click event.
   */
  const handleClick = useCallback((event) => {
    const { onClick: latestOnClick } = callbacksRef.current;
    if (typeof latestOnClick === 'function') latestOnClick(event);
  }, []);

  /**
   * Stable wrapper for the ECE `confirm` event; forwards to (and returns) the latest onConfirm.
   * @param {Object} event - ECE confirm event.
   */
  const handleConfirm = useCallback((event) => {
    const { onConfirm: latestOnConfirm } = callbacksRef.current;
    if (typeof latestOnConfirm === 'function') return latestOnConfirm(event);
    return undefined;
  }, []);

  /**
   * Stable wrapper for the ECE `cancel` event; forwards to the latest onCancel.
   * @param {Object} event - ECE cancel event.
   */
  const handleCancel = useCallback((event) => {
    const { onCancel: latestOnCancel } = callbacksRef.current;
    if (typeof latestOnCancel === 'function') latestOnCancel(event);
  }, []);

  /**
   * Resolves wallet availability from the ECE `ready` event.
   * When the wallet is unavailable (browser/device/currency unsupported) the method must not
   * be offered, mirroring the Adyen express wallet behavior.
   * @param {Object} event - ECE ready event with `availablePaymentMethods`.
   */
  const handleReady = useCallback(
    (event) => {
      const walletAvailable = Boolean(event?.availablePaymentMethods?.[walletKey]);

      setIsAvailable(walletAvailable);
      if (!walletAvailable) {
        setErrorState(`${errorPrefix}_not_available`);
      }
      setIsLoading(false);

      const { onReady: latestOnReady } = callbacksRef.current;
      if (typeof latestOnReady === 'function') latestOnReady(event);
    },
    [walletKey, errorPrefix],
  );

  /**
   * Mount the Express Checkout Element when the container and config are ready.
   * Mounts once; the wallet callbacks are read via refs and amount changes go through
   * updateExpressAmount, so neither remounts the element.
   */
  useEffect(() => {
    let cancelled = false;

    if (!containerRef.current) return undefined;
    if (isMountedRef.current) return undefined;

    if (!enabled) {
      setErrorState(`${errorPrefix}_not_enabled`);
      setIsLoading(false);
      return undefined;
    }

    if (!amount || !currency) return undefined;

    /**
     * Mounts the ECE once the Stripe SDK finishes loading.
     */
    const mount = async () => {
      let stripe;
      try {
        stripe = await stripeEngine.waitForStripe();
      } catch (e) {
        // loadStripe rejects when the SDK script fails to load (network/CSP);
        // surface the error instead of leaving the button stuck loading.
        if (!cancelled) {
          setErrorState('stripe_not_initialized');
          setIsLoading(false);
        }
        return;
      }

      if (cancelled) return;

      if (!stripe) {
        setErrorState('stripe_not_initialized');
        setIsLoading(false);
        return;
      }

      // Read the freshest values now (not the closure's), in case the total changed while
      // waitForStripe() was pending.
      const { amount: currentAmount, currency: currentCurrency } = latestConfigRef.current;

      const expressCheckoutElement = stripeEngine.mountExpressCheckoutElement(
        containerRef.current,
        {
          paymentMethods,
          amount: currentAmount,
          currency: currentCurrency,
          appearance: appearance || {},
          locale,
          onClick: handleClick,
          onConfirm: handleConfirm,
          onCancel: handleCancel,
          onReady: handleReady,
        },
      );

      if (!expressCheckoutElement || cancelled) {
        if (!expressCheckoutElement) {
          setErrorState(`${errorPrefix}_initialization_error`);
          setIsLoading(false);
        }
        return;
      }

      mountedConfig.current = { amount: currentAmount, currency: currentCurrency };
      isMountedRef.current = true;
    };

    setIsLoading(true);
    setErrorState(null);
    mount();

    return () => {
      cancelled = true;

      if (isMountedRef.current) {
        stripeEngine.unmountExpressCheckoutElement();
        isMountedRef.current = false;
        mountedConfig.current = { amount: null, currency: null };
        setIsAvailable(false);
      }
    };
    // Raw amount updates are handled by the effect below without remounting the ECE;
    // hasAmount only flips on the falsy -> valid transition so the ECE can mount once
    // the total is known. The wallet callbacks (handleClick/handleConfirm/handleCancel/
    // handleReady) are stable refs, so they intentionally do not remount the element.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, currency, hasAmount, locale, appearance]);

  /**
   * Update the ECE amount when the purchase total changes, without remounting.
   */
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (!amount || !currency) return;

    const amountChanged = mountedConfig.current.amount !== amount;
    const currencyChanged = mountedConfig.current.currency !== currency;
    if (!amountChanged && !currencyChanged) return;

    stripeEngine.updateExpressAmount(amount, currency);
    mountedConfig.current = { amount, currency };
  }, [amount, currency]);

  return {
    containerRef,
    isLoading,
    error,
    isAvailable,
  };
}

export default useStripeExpressWalletSdk;
